import type { KeyboardEvent as TeclaEvento } from "react";
import type { EstadoNodo, NodoGrafo, RolNodo } from "./definicion";
import { posicionDe } from "./definicion";

/**
 * Un nodo del circuito, dibujado a partir de su POSICIÓN, su `rol` y su
 * `estado` — nunca de su `id`. El mapa de estilos es lo que más fácil se
 * dibuja mal, así que va explícito
 * (ADR-2026-08-16-estudio-marketing-mapa-grafo-declarativo).
 */

interface Estilo {
  relleno: string;
  borde: string | null;
  grosor: number;
  punteado: string | null;
  texto: string;
  /** Si true, la etiqueta va ADENTRO del círculo (nodos grandes). */
  etiquetaAdentro: boolean;
  /** Si true, el CÍRCULO lleva sombra. Ver `SOMBRA_CIRCULO`. */
  sombra: boolean;
}

const POR_ROL: Record<RolNodo, Estilo> = {
  agente:    { relleno: "#FFFFFF", borde: "var(--brand-primary)", grosor: 1.5, punteado: null, texto: "#3A4362", etiquetaAdentro: false, sombra: true },
  compuerta: { relleno: "#FFFFFF", borde: "var(--brand-primary)", grosor: 2.6, punteado: null, texto: "var(--brand-primary)", etiquetaAdentro: false, sombra: true },
  humano:    { relleno: "var(--brand-black)", borde: null,      grosor: 0,   punteado: null, texto: "#FFFFFF", etiquetaAdentro: true,  sombra: true },
  fuente:    { relleno: "#EDEFF5", borde: "#D8DCE6", grosor: 1,   punteado: null, texto: "#3A4362", etiquetaAdentro: false, sombra: true },
  memoria:   { relleno: "#F4F5FD", borde: "#B9B5E8", grosor: 1.6, punteado: null, texto: "#5B57A8", etiquetaAdentro: false, sombra: true },
  global:    { relleno: "#EDEFF5", borde: "#C2C8D6", grosor: 1,   punteado: null, texto: "#767D96", etiquetaAdentro: false, sombra: true },
};

/**
 * La sombra de los nodos: negro de marca (var(--brand-black)) con muy poca alfa, apenas
 * corrida hacia abajo. Despega el círculo del fondo de puntos sin agregar un
 * material nuevo a una pantalla que es un diagrama plano.
 *
 * POR QUÉ `filter: drop-shadow(...)` DE CSS Y NO UN `<filter>` SVG CON
 * `<feDropShadow>`:
 *  - el `<filter>` de SVG trae región por defecto `-10% / 120%` del bounding
 *    box del elemento. Para el nodo más chico del mapa (r=14 → bbox 28×28) eso
 *    da 2.8 unidades de margen, y esta sombra ya se extiende ~4 (1 de
 *    desplazamiento + ~3 de blur): la recortaría en seco por abajo. Habría que
 *    declarar `x/y/width/height` a mano y recalcularlos cada vez que alguien
 *    cambie el radio de un nodo en `definicion.ts`. Con `drop-shadow` la región
 *    la calcula el motor y no hay nada que mantener.
 *  - el `<filter>` habría que declararlo en los `<defs>` de `CircuitoMapa` y
 *    referenciarlo por id desde acá, partiendo en dos archivos una decisión que
 *    es puramente de estilo del nodo.
 *
 * Y NO INTERACTÚA CON LOS GLIFOS: el filtro va sobre el `<circle>`, que es
 * HERMANO del `<use href="#glifo-...">`, no su ancestro. El caso feo conocido
 * —un filtro sobre un ancestro del `<use>`, que reabre el recorte del viewport
 * del `<symbol>` y termina comiéndose el glifo pese al `overflow="visible"`—
 * no puede darse: ningún `<use>` queda adentro del subárbol filtrado.
 *
 * Los valores están en unidades del viewBox (las funciones de filtro sobre
 * elementos SVG resuelven sus longitudes en el sistema de coordenadas del
 * usuario), así que la sombra ESCALA con el mapa igual que todo lo demás. A los
 * tamaños en que se dibuja la pantalla el factor va de ~0.4× (mobile) a ~1.2×
 * (desktop), y a 1px/2px el resultado se sostiene en toda esa banda: por eso se
 * dejaron los valores de arranque en vez de subirlos. Con alfa 0.10 la sombra
 * queda apenas por encima del fondo de puntos (var(--brand-black) al 0.07), que es
 * exactamente lo que se busca — que se lea como apoyo y no como una capa nueva.
 */
const SOMBRA_CIRCULO = "drop-shadow(0 1px 2px rgb(var(--brand-black-rgb) / 0.10))";

/**
 * Los nodos del anillo se ven todos igual: el circuito se lee como una sola
 * pieza y ninguna etapa pesa más que otra. La condición es GEOMÉTRICA —
 * `posicion.tipo === "anillo"` — y no por rol: los roles los comparten nodos
 * de afuera del anillo (`numeros` es `fuente` igual que Instagram, `vos` es
 * `humano`), que tienen que seguir viéndose como hasta ahora
 * (ADR-2026-08-16-mapa-anillo-uniforme-por-posicion).
 */
const ESTILO_ANILLO: Estilo = {
  relleno: "#FFFFFF", borde: "var(--brand-primary)", grosor: 1.5, punteado: null, texto: "#3A4362",
  etiquetaAdentro: false, sombra: true,
};

/** El estado pisa a la base: un nodo se ve encendido solo si lo que representa
 *  existe. Por eso `sin-conectar`/`no-existe` se resuelven después de elegir la
 *  base y la sobrescriben, valga esa base para el anillo o para el rol.
 *
 *  La sombra sigue la misma regla y se apaga en los dos: un nodo apagado se
 *  dibuja punteado justamente para leerse como AUSENTE, y una sombra lo levanta
 *  del fondo y lo devuelve al plano de los que sí están. Sombra sólo para
 *  `activo`.
 *
 *  `desconocido` NO se puede dibujar como `sin-conectar`: el punteado es la
 *  marca de "esto no está", y usarlo cuando la consulta no volvió afirma algo
 *  que nadie sabe (y contradice a la pantalla de datos, que en ese mismo caso
 *  dice "No pudimos consultarlo"). Se dibuja con el TRAZO ENTERO —el nodo está
 *  ahí— pero apagado y sin sombra: presente, sin afirmar nada. Tampoco va en
 *  rojo: es el estado en el que arranca cada carga del mapa, y encender una
 *  alarma en cada visita gastaría la señal. El aviso lo da la píldora del
 *  encabezado, que en el caso de fallo lo dice con todas las letras. */
function estiloDe(rol: RolNodo, estado: EstadoNodo, enAnillo: boolean): Estilo {
  const base = enAnillo ? ESTILO_ANILLO : POR_ROL[rol];
  if (estado === "sin-conectar") {
    return { ...base, borde: "#D8DCE6", punteado: "3 3", texto: "#AEB5C6", sombra: false };
  }
  if (estado === "no-existe") {
    return { ...base, relleno: "#FFFFFF", borde: "#D8DCE6", grosor: 1.3, punteado: "3 3",
             texto: "#C4CAD8", sombra: false };
  }
  if (estado === "desconocido") {
    return { ...base, relleno: "#EDEFF5", borde: "#C2C8D6", grosor: 1, punteado: null,
             texto: "#767D96", sombra: false };
  }
  return base;
}

type LadoEtiqueta = "arriba" | "abajo" | "izquierda" | "derecha";

/** Dónde va la etiqueta (y su `sub`, si tiene) respecto del círculo. Los
 *  casos verticales son la mayoría; izquierda/derecha los usan las fuentes, las
 *  memorias "para hacer" y los dos nodos del anillo que caen en sus FLANCOS
 *  —donde la elipse corre casi vertical y una etiqueta centrada queda partida
 *  por el trazo—, que la mandan hacia adentro. El lado sale siempre del dato
 *  (`etiquetaEn`): acá no se decide por id ni por posición. */
function posicionEtiqueta(x: number, y: number, radio: number, lado: LadoEtiqueta) {
  const gap = 14;
  if (lado === "arriba") return { x, y: y - radio - gap, anchor: "middle" as const, dySub: -11 };
  if (lado === "izquierda") return { x: x - radio - 8, y: y + 3, anchor: "end" as const, dySub: 11 };
  if (lado === "derecha") return { x: x + radio + 8, y: y + 3, anchor: "start" as const, dySub: 11 };
  return { x, y: y + radio + gap + 2, anchor: "middle" as const, dySub: 11 };
}

export const NodoCircuito = ({
  nodo,
  onNavegar,
}: {
  nodo: NodoGrafo;
  onNavegar: (ruta: string) => void;
}) => {
  const { x, y } = posicionDe(nodo);
  const e = estiloDe(nodo.rol, nodo.estado, nodo.posicion.tipo === "anillo");
  const clickeable = nodo.destino !== null;

  const interaccion = clickeable
    ? {
        role: "button" as const,
        tabIndex: 0,
        className:
          "cursor-pointer outline-none rounded-full focus-visible:outline focus-visible:outline-2 " +
          "focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
        onClick: () => onNavegar(nodo.destino as string),
        onKeyDown: (ev: TeclaEvento) => {
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            onNavegar(nodo.destino as string);
          }
        },
      }
    : {};

  // El glifo escala con el radio del nodo: los símbolos están dibujados para
  // un radio de referencia de 22 (el más común entre los agentes).
  const escalaGlifo = nodo.radio / 22;
  const yGlifo = e.etiquetaAdentro ? y - nodo.radio * 0.26 : y;

  const lado = nodo.etiquetaEn ?? "abajo";
  const etiqueta = posicionEtiqueta(x, y, nodo.radio, lado);

  return (
    <g {...interaccion} aria-label={nodo.etiqueta}>
      {!clickeable && <title>{`${nodo.etiqueta} — todavía no existe`}</title>}
      <circle
        cx={x}
        cy={y}
        r={nodo.radio}
        strokeWidth={e.grosor || undefined}
        strokeDasharray={e.punteado ?? undefined}
        // Sobre el CÍRCULO y no sobre el `<g>`: el grupo también contiene el
        // glifo y los textos de la etiqueta, y un filtro ahí les tira sombra a
        // las letras — a 9px de tipografía eso no se lee como relieve, se lee
        // como texto sucio.
        // Relleno y borde por `style` y no por atributo: son variables CSS del branding.
        style={{ fill: e.relleno, stroke: e.borde ?? undefined, filter: e.sombra ? SOMBRA_CIRCULO : undefined }}
      />
      <use
        href={`#glifo-${nodo.glifo}`}
        transform={`translate(${x} ${yGlifo}) scale(${escalaGlifo})`}
        style={{ color: e.texto }}
      />
      {e.etiquetaAdentro ? (
        <text x={x} y={y + nodo.radio * 0.42} textAnchor="middle" fontSize={9.5}
              fontWeight={600} style={{ fill: e.texto }}>
          {nodo.etiqueta}
        </text>
      ) : (
        <>
          <text x={etiqueta.x} y={etiqueta.y} textAnchor={etiqueta.anchor} fontSize={9} style={{ fill: e.texto }}>
            {nodo.etiqueta}
          </text>
          {nodo.sub && (
            <text x={etiqueta.x} y={etiqueta.y + etiqueta.dySub} textAnchor={etiqueta.anchor}
                  fontSize={7.5} fill="#AEB5C6">
              {nodo.sub}
            </text>
          )}
        </>
      )}
    </g>
  );
};

export default NodoCircuito;
